// src/pages/finance-portal/fixed-assets/Depreciation.js
//
// Depreciation runs and Section 179 optimizer

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper,
    Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK,
    formatCurrency, formatDate, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = { fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' };
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function Depreciation() {
    const { enqueueSnackbar } = useSnackbar();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(currentPeriod());
    const [bookType, setBookType] = useState('BOOK');
    const [running, setRunning] = useState(false);
    const [dryRun, setDryRun] = useState(null);

    // Section 179
    const [s179Year, setS179Year] = useState(new Date().getFullYear());
    const [s179Loading, setS179Loading] = useState(false);
    const [s179Result, setS179Result] = useState(null);
    const [adviceLoading, setAdviceLoading] = useState(false);
    const [advice, setAdvice] = useState('');

    const fetchRuns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/fixed-assets/depreciation/runs',
                { params: { per_page: 50 } });
            if (res.data?.success) setRuns(res.data.data || []);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Failed to load runs', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => { fetchRuns(); }, [fetchRuns]);

    const handleDryRun = async () => {
        setRunning(true);
        setDryRun(null);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/depreciation/run', {
                period_label: period, book_type: bookType, dry_run: true,
            });
            if (res.data?.success) setDryRun(res.data);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Dry run failed', { variant: 'error' });
        } finally {
            setRunning(false);
        }
    };

    const handlePost = async () => {
        if (!window.confirm(`Post depreciation for ${period} (${bookType})?`)) return;
        setRunning(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/depreciation/run', {
                period_label: period, book_type: bookType, dry_run: false, auto_post_je: true,
            });
            if (res.data?.success) {
                enqueueSnackbar(`Posted ${res.data.data?.run?.asset_count || 0} entries`, { variant: 'success' });
                setDryRun(null);
                fetchRuns();
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Post failed', { variant: 'error' });
        } finally {
            setRunning(false);
        }
    };

    const handleS179 = async () => {
        setS179Loading(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/depreciation/section-179-optimizer',
                { year: parseInt(s179Year, 10) });
            if (res.data?.success) setS179Result(res.data.data);
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Optimizer failed', { variant: 'error' });
        } finally {
            setS179Loading(false);
        }
    };

    const handleAiAdvice = async () => {
        setAdviceLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/fixed-assets/agent/section-179-advice',
                { year: parseInt(s179Year, 10) });
            if (res.data?.success) setAdvice(res.data.data?.narrative || '');
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Advice failed', { variant: 'error' });
        } finally {
            setAdviceLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CalculateIcon sx={{ fontSize: 30, color: '#fff' }} />
                    <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                        Depreciation Engine
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                                    Run Monthly Depreciation
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField fullWidth size="small" label="Period (YYYY-MM)" value={period}
                                            onChange={e => setPeriod(e.target.value)}
                                            InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                            InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth size="small" select label="Book Type" value={bookType}
                                            onChange={e => setBookType(e.target.value)}
                                            InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                            InputLabelProps={{ sx: { fontFamily: FA_FONT } }}>
                                            {['BOOK', 'TAX', 'AMT', 'ACE'].map(b => (
                                                <MenuItem key={b} value={b} sx={{ fontFamily: FA_FONT }}>{b}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button variant="outlined" onClick={handleDryRun} disabled={running}
                                                sx={{ fontFamily: FA_FONT, textTransform: 'none', borderRadius: '10px',
                                                    borderColor: FA_ACCENT, color: FA_ACCENT }}>
                                                {running ? 'Running...' : 'Preview (Dry Run)'}
                                            </Button>
                                            <Button variant="contained" startIcon={<PlayCircleFilledIcon />}
                                                onClick={handlePost} disabled={running}
                                                sx={{ fontFamily: FA_FONT, textTransform: 'none', borderRadius: '10px',
                                                    bgcolor: FA_ACCENT, '&:hover': { bgcolor: FA_ACCENT_DARK } }}>
                                                Post Period
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>

                                {dryRun && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: '12px' }}>
                                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: '#0f172a' }}>
                                            Preview: {dryRun.entry_count} assets, total {formatCurrency(dryRun.total_depreciation)}
                                        </Typography>
                                        <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                                            {(dryRun.entries || []).map(e => (
                                                <Box key={e.asset_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.82rem' }}>
                                                        {e.asset_number} {e.asset_name}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.82rem', fontWeight: 600 }}>
                                                        {formatCurrency(e.depreciation_amount)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                                    Section 179 / Bonus Optimizer
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                    <TextField size="small" type="number" label="Year"
                                        value={s179Year} onChange={e => setS179Year(e.target.value)}
                                        InputProps={{ sx: { fontFamily: FA_FONT, borderRadius: '10px' } }}
                                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }} />
                                    <Button variant="contained" onClick={handleS179} disabled={s179Loading}
                                        sx={{ fontFamily: FA_FONT, textTransform: 'none', borderRadius: '10px',
                                            bgcolor: FA_ACCENT, '&:hover': { bgcolor: FA_ACCENT_DARK } }}>
                                        {s179Loading ? 'Computing...' : 'Optimize'}
                                    </Button>
                                    <Button variant="outlined" startIcon={<AutoFixHighIcon />}
                                        onClick={handleAiAdvice} disabled={adviceLoading}
                                        sx={{ fontFamily: FA_FONT, textTransform: 'none', borderRadius: '10px',
                                            borderColor: FA_ACCENT, color: FA_ACCENT }}>
                                        {adviceLoading ? '...' : 'AI Memo'}
                                    </Button>
                                </Box>
                                {s179Result && (
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px' }}>
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.85rem', color: '#475569' }}>
                                            Effective Section 179 limit: <b>{formatCurrency(s179Result.effective_limit)}</b>
                                        </Typography>
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.85rem', color: '#475569' }}>
                                            Total recommended: <b>{formatCurrency(s179Result.total_section_179)}</b> Section 179 +
                                            <b> {formatCurrency(s179Result.total_bonus_depreciation)}</b> bonus =
                                            <b> {formatCurrency(s179Result.first_year_deduction)}</b> first-year deduction
                                        </Typography>
                                    </Box>
                                )}
                                {advice && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: '#fef3c7', borderRadius: '12px', border: '1px solid #fde68a' }}>
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>
                                            {advice}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.95rem', fontWeight: 700, color: FA_ACCENT_DARK, mb: 2 }}>
                                    Recent Runs
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {['Period', 'Book', 'Assets', 'Total Depreciation', 'Status', 'Posted At', 'JE'].map(h => (
                                                    <TableCell key={h} sx={headSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                [1, 2, 3].map(i => (
                                                    <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                                                ))
                                            ) : runs.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No runs yet</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : runs.map(r => (
                                                <TableRow key={r.id}>
                                                    <TableCell sx={cellSx}>{r.period_label}</TableCell>
                                                    <TableCell sx={cellSx}>{r.book_type}</TableCell>
                                                    <TableCell sx={cellSx}>{r.asset_count}</TableCell>
                                                    <TableCell sx={cellSx}>{formatCurrency(r.total_depreciation)}</TableCell>
                                                    <TableCell sx={cellSx}>
                                                        <Chip size="small" label={r.is_posted ? 'Posted' : 'Pending'}
                                                            sx={{ fontFamily: FA_FONT,
                                                                bgcolor: r.is_posted ? '#dcfce7' : '#fef3c7',
                                                                color: r.is_posted ? '#166534' : '#92400e' }} />
                                                    </TableCell>
                                                    <TableCell sx={cellSx}>{formatDate(r.posted_at)}</TableCell>
                                                    <TableCell sx={cellSx}>{r.journal_entry_id || '\u2014'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
